import React, { useEffect, useState, Fragment } from "react";
import { Table, Button, Card, Row, Col, Form, Dropdown, Modal } from "react-bootstrap";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../api";
import swal from "sweetalert";
import PageTitle from "../../layouts/PageTitle";
import * as XLSX from "xlsx";
import BoqImportModal from "./BoqImportModal";


const AutoResizeTextarea = ({ value, onChange, placeholder = "", className = "", minHeight = 44, ...props }) => {
    const textareaRef = React.useRef(null);

    const adjustHeight = () => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
    };

    useEffect(() => {
        adjustHeight();
    }, [value]);

    return (
        <textarea
            ref={textareaRef}
            className={`form-control ${className}`}
            value={value ?? ""}
            onChange={(e) => {
                if (onChange) onChange(e);
                adjustHeight();
            }}
            placeholder={placeholder}
            rows={1}
            style={{
                resize: "none",
                minHeight: `${minHeight}px`,
                lineHeight: "1.4",
                overflowY: "hidden",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "12.5px",
                padding: "6px 8px",
                ...props.style,
            }}
            {...props}
        />
    );
};

const WorkOrderBOQ = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const projectFromUrl = searchParams.get("project") || "";

    const [boqItems, setBoqItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [projectName, setProjectName] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [paymentMode, setPaymentMode] = useState("STANDARD");
    const [sacCode, setSacCode] = useState("995461");

    const pageSize = 10;

    const [descriptionModal, setDescriptionModal] = useState({
        show: false,
        itemIndex: null,
        itemName: "",
        description: "",
    });

    const [itemNameModal, setItemNameModal] = useState({
        show: false,
        itemIndex: null,
        itemNumber: null,
        itemName: "",
    });

    const openDescriptionModal = (index) => {
        const item = boqItems[index];
        if (!item) return;
        setDescriptionModal({
            show: true,
            itemIndex: index,
            itemName: item.itemName || `Item #${index + 1}`,
            description: item.description || "",
        });
    };

    const handleSaveDescriptionModal = () => {
        if (descriptionModal.itemIndex !== null) {
            updateField(descriptionModal.itemIndex, "description", descriptionModal.description);
        }
        setDescriptionModal({ show: false, itemIndex: null, itemName: "", description: "" });
    };

    const openItemNameModal = (index) => {
        const item = boqItems[index];
        if (!item) return;
        setItemNameModal({
            show: true,
            itemIndex: index,
            itemNumber: index + 1,
            itemName: item.itemName || "",
        });
    };

    const handleSaveItemNameModal = () => {
        if (itemNameModal.itemIndex !== null) {
            updateField(itemNameModal.itemIndex, "itemName", itemNameModal.itemName);
        }
        setItemNameModal({ show: false, itemIndex: null, itemNumber: null, itemName: "" });
    };

    const paymentStages = [
        "Supply",
        "Installation",
        "Testing & Commissioning",
        "Handover",
    ];
    const defaultPaymentTerms = [{ stage: "", name: "", percentage: 0 }];

    const [hasAdvance, setHasAdvance] = useState(false);
    const [advanceAmount, setAdvanceAmount] = useState(0);
    const [advanceDescription, setAdvanceDescription] = useState("");

    const [showPaymentTerms, setShowPaymentTerms] = useState(false);
    const [paymentTermTargetIndex, setPaymentTermTargetIndex] = useState(null);
    const [units, setUnits] = useState([]);
    const [showBoqImport, setShowBoqImport] = useState(false);

    // ── Payment Groups state ──────────────────────────────────────────────────
    const [paymentGroups, setPaymentGroups] = useState([]);
    // activeGroupIndex: null = editing global/fallback terms, number = editing that group's terms
    const [activeGroupIndex, setActiveGroupIndex] = useState(null);
    // ─────────────────────────────────────────────────────────────────────────
    const totalPages = Math.ceil(boqItems.length / pageSize);
    // ================= GST STATES =================
    const [cgst, setCgst] = useState(0);
    const [sgst, setSgst] = useState(0);
    const [igst, setIgst] = useState(0);

    const [cgstAmount, setCgstAmount] = useState(0);
    const [sgstAmount, setSgstAmount] = useState(0);
    const [igstAmount, setIgstAmount] = useState(0);

    const [finalTotal, setFinalTotal] = useState(0);

    const paginatedItems = boqItems.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
    );

    const [paymentTerms, setPaymentTerms] = useState(defaultPaymentTerms);

    const normalizeTerms = (terms) =>
        (Array.isArray(terms) ? terms : [])
            .map((term) => ({
                stage: term.stage || "",
                name: term.name || "",
                percentage: Number(term.percentage || 0),
            }))
            .filter((term) => term.stage || term.name || term.percentage);

    useEffect(() => {
        const totalPages = Math.ceil(boqItems.length / pageSize);
        if (currentPage > totalPages) {
            setCurrentPage(totalPages || 1);
        }
    }, [boqItems]);

    useEffect(() => {
        loadBOQ();
        fetchUnits();
    }, [id]);

    const loadBOQ = async () => {
        try {
            const res = await api.get(`/work-orders/${id}`);
            const wo = res.data?.data;
            if (wo?.sacCode) setSacCode(wo.sacCode);
            // 🔥 GST LOGIC (COPY FROM BILLING)
            const woData = wo;

            const clientState =
                woData.project?.client?.billingAddress?.state ||
                woData.project?.client?.shippingAddress?.state ||
                "";

            const office = woData.project?.office || {};
            let companyState = office.state || "";
            if (!companyState && office.address) {
                const addr = office.address.toLowerCase();
                if (addr.includes("uttar pradesh") || addr.includes("noida")) companyState = "Uttar Pradesh";
                else if (addr.includes("maharashtra") || addr.includes("mumbai")) companyState = "Maharashtra";
            }
            if (!companyState) companyState = "Maharashtra";

            const normalizeState = (s) => (s || "").toString().trim().toLowerCase();
            if (
                clientState &&
                normalizeState(clientState) === normalizeState(companyState)
            ) {
                // SAME STATE → CGST + SGST
                setCgst(9);
                setSgst(9);
                setIgst(0);
            } else {
                // DIFFERENT STATE → IGST
                setCgst(0);
                setSgst(0);
                setIgst(18);
            }

            if (!wo) {
                swal("Error", "Work order not found", "error");
                return;
            }

            setProjectName(
                wo.project?.projectCode
                    ? `${wo.project.projectCode} – ${wo.project.projectName}`
                    : wo.project?.projectName || "",
            );

            setBoqItems(
                (wo.boqItems || []).map((item) => ({
                    _id: item._id || null,
                    wlino: item.wlino || "",
                    hsnSac: item.hsnSac || wo.sacCode || "995461",
                    itemName: item.itemName || "",
                    description: item.description || "",
                    unit: item.unit || "",
                    quantity: Number(item.quantity) || 0,
                    price: Number(item.price) || 0,
                    paymentMode: item.paymentMode || "STANDARD",
                    paymentTerms: normalizeTerms(item.paymentTerms),
                })),
            );

            // ✅ Load paymentGroups from WO so group-assigned items are tracked
            setPaymentGroups(Array.isArray(wo.paymentGroups) ? wo.paymentGroups : []);

            // ✅ Load Advance Payment settings
            setAdvanceAmount(wo.advanceAmount || 0);
            setAdvanceDescription(wo.advanceDescription || "");
            setHasAdvance(Boolean(wo.advanceAmount || wo.advanceDescription));

        } catch (error) {
            swal("Error", "Failed to load BOQ", "error");
        }
    };
    const addPaymentRow = () => {
        setPaymentTerms([...paymentTerms, { stage: "", name: "", percentage: 0 }]);
    };

    const removePaymentRow = (index) => {
        setPaymentTerms(paymentTerms.filter((_, i) => i !== index));
    };
    const fetchUnits = async () => {
        try {
            const res = await api.get("/boq-unit-types"); // Active only
            if (res.data.success) {
                setUnits(res.data.data);
            }
        } catch (err) {
            swal("Error", "Failed to load BOQ units", "error");
        }
    };

    const updatePaymentTerm = (index, field, value) => {
        const updated = [...paymentTerms];
        updated[index][field] = field === "percentage" ? Number(value) : value;
        setPaymentTerms(updated);
    };

    const totalPercentage = paymentTerms.reduce(
        (sum, row) => sum + Number(row.percentage || 0),
        0,
    );

    // ---------------------------------------------------
    // ADD ROW
    // ---------------------------------------------------
    const addRow = () => {
        setBoqItems([
            ...boqItems,
            {
                _id: null, // New item
                wlino: "",
                hsnSac: sacCode,

                itemName: "",
                description: "",
                unit: "",
                quantity: 0,
                price: 0,
                paymentMode: paymentMode || "STANDARD",
                paymentTerms: normalizeTerms(paymentTerms),
            },
        ]);
    };

    // ---------------------------------------------------
    // REMOVE ROW
    // ---------------------------------------------------
    const removeRow = (index) => {
        const updated = boqItems.filter((_, i) => i !== index);
        setBoqItems(updated);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // only select items with an _id
            const allWithId = boqItems
                .filter((item) => item._id)
                .map((item) => item._id);
            setSelectedItems(allWithId);
        } else {
            setSelectedItems([]);
        }
    };

    const handleSelectOne = (id) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter((i) => i !== id));
        } else {
            setSelectedItems([...selectedItems, id]);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedItems.length === 0) return;

        const confirm = await swal({
            title: "Are you sure?",
            text: `You are about to delete ${selectedItems.length} BOQ items from the database!`,
            icon: "warning",
            buttons: true,
            dangerMode: true,
        });

        if (confirm) {
            try {
                const res = await api.post(`/work-orders/${id}/boq/bulk-delete`, {
                    itemIds: selectedItems,
                });

                if (res.data.success) {
                    swal("Deleted!", `${selectedItems.length} items removed`, "success");
                    setSelectedItems([]);
                    loadBOQ(); // Reload items from server
                }
            } catch (err) {
                console.error("Bulk delete error:", err);
                swal("Error", err.response?.data?.message || "Failed to delete BOQ items", "error");
            }
        }
    };

    // ---------------------------------------------------
    // PDF DOWNLOAD
    // ---------------------------------------------------
    const downloadBOQPDF = async () => {
        try {
            const res = await api.get(`/work-orders/${id}/download-boq-pdf`, {
                responseType: "blob",
            });

            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `BOQ-${projectName || "WorkOrder"}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.setTimeout(() => window.URL.revokeObjectURL(url), 3000);
        } catch (error) {
            console.error(error);
            swal("Error", "BOQ PDF download failed", "error");
        }
    };
    // ---------------------------------------------------
    // EXCEL DOWNLOAD (REAL BOQ)
    // ---------------------------------------------------
    const downloadBOQExcel = async () => {
        try {
            const res = await api.get(`/work-orders/${id}/download-boq-excel`, {
                responseType: "blob",
            });

            const blob = new Blob([res.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `BOQ-${projectName || "WorkOrder"}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            window.setTimeout(() => window.URL.revokeObjectURL(url), 3000);
        } catch (error) {
            console.error(error);
            swal("Error", "BOQ Excel download failed", "error");
        }
    };
    // ---------------------------------------------------
    // SAMPLE EXCEL DOWNLOAD
    // ---------------------------------------------------
    const downloadSampleExcel = () => {
        const sample = [
            {
                wlino: "1.1",
                hsnSac: "2523",
                itemName: "Cement",

                description: "OPC Grade",
                unit: "kg",
                quantity: 100,
                price: 450,
            },
            {
                wlino: "1.2",
                hsnSac: "25233",
                itemName: "Steel Rod",
                description: "TMT bar 12mm",
                unit: "meter",
                quantity: 500,
                price: 65,
            },
        ];

        const ws = XLSX.utils.json_to_sheet(sample);

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "BOQ Sample");

        XLSX.writeFile(wb, "BOQ-Sample.xlsx");
    };

    // ---------------------------------------------------
    // IMPORT EXCEL
    // ---------------------------------------------------
    const importExcel = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const workbook = XLSX.read(bstr, { type: "binary" });
                const wsname = workbook.SheetNames[0];
                const ws = workbook.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                if (!data.length) {
                    swal("Warning", "Excel file is empty", "warning");
                    return;
                }

                const imported = data.map((row) => ({
                    _id: null, // Imported items are new
                    wlino: row.wlino || "",
                    hsnSac: row.hsnSac || "",

                    itemName: row.itemName || "",
                    description: row.description || "",
                    unit: row.unit || "",
                    quantity: Number(row.quantity) || 0,
                    price: Number(row.price) || 0,
                    paymentMode: "STANDARD",
                    paymentTerms: [],
                }));

                setBoqItems(imported);
                swal("Success", "Excel imported successfully", "success");
            } catch (err) {
                console.error(err);
                swal("Error", "Invalid Excel format", "error");
            }
        };

        reader.readAsBinaryString(file);
    };

    const handleApplyImportedBoq = async (importedItems, meta = {}) => {
        if (!Array.isArray(importedItems) || importedItems.length === 0) {
            swal("Warning", "No BOQ rows were parsed", "warning");
            return;
        }

        const validCount = importedItems.filter(
            (item) =>
                item.itemName &&
                item.description &&
                item.unit &&
                Number(item.quantity) > 0 &&
                Number(item.price) >= 0,
        ).length;

        const confirm = await swal({
            title: "Replace current BOQ?",
            text:
                boqItems.length > 0
                    ? `This will replace ${boqItems.length} current rows with ${importedItems.length} imported rows from ${meta.sourceFile || "the PDF"}.`
                    : `Import ${importedItems.length} rows from ${meta.sourceFile || "the PDF"}?`,
            icon: "warning",
            buttons: ["Cancel", "Import"],
            dangerMode: true,
        });

        if (!confirm) return;

        setBoqItems(
            importedItems.map((item) => ({
                _id: null,
                wlino: item.wlino || "",
                hsnSac: item.hsnSac || sacCode,
                itemName: item.itemName || "",
                description: item.description || "",
                unit: item.unit || "",
                quantity: Number(item.quantity) || 0,
                price: Number(item.price) || 0,
                paymentMode: "STANDARD",
                paymentTerms: [],
            })),
        );
        setCurrentPage(1);

        swal(
            "Imported",
            `${importedItems.length} rows loaded into the BOQ table (${validCount} ready, ${importedItems.length - validCount} need review).`,
            validCount === importedItems.length ? "success" : "warning",
        );
    };
    // ---------------------------------------------------
    // UPDATE FIELD
    // ---------------------------------------------------
    const updateField = (index, field, value) => {
        const updated = [...boqItems];
        if (field === "quantity" || field === "price") value = Number(value);
        updated[index][field] = value;
        setBoqItems(updated);
    };

    // ---------------------------------------------------
    // SAVE BOQ
    // ---------------------------------------------------
    const saveBOQ = async () => {
        if (!boqItems.length) {
            swal("Error", "Please add at least one BOQ item", "error");
            return;
        }

        for (let item of boqItems) {
            if (!item.itemName.trim()) {
                swal("Error", "Item name cannot be empty", "error");
                return;
            }
            if (!item.unit) {
                swal("Error", "Unit is required", "error");
                return;
            }

            if (isNaN(item.quantity) || item.quantity <= 0) {
                swal("Error", "Quantity must be a positive number", "error");
                return;
            }
            if (isNaN(item.price) || Number(item.price) < 0 || item.price === "" || item.price === null || item.price === undefined) {
                swal("Error", "Price cannot be negative or empty", "error");
                return;
            }
        }

        try {
            await api.patch(`/work-orders/${id}/boq`, { boqItems });
            swal("Success", "BOQ updated successfully", "success");
            navigate(
                projectFromUrl
                    ? `/work-orders/${id}/boq?project=${projectFromUrl}`
                    : `/work-orders/${id}/boq`,
            );
        } catch (error) {
            swal(
                "Error",
                error.response?.data?.message || "Failed to save BOQ",
                "error",
            );
        }
    };
    // ---------------------------------------------------
    // SAVE PAYMENT TERMS
    // ---------------------------------------------------
    const savePaymentTerms = async () => {
        try {
            const cleanedTerms = normalizeTerms(paymentTerms);

            // ── Case 1: Saving individual BOQ item terms ────────────────────────
            if (paymentTermTargetIndex !== null) {
                const updated = boqItems.map((item, index) =>
                    index === paymentTermTargetIndex
                        ? { ...item, paymentMode, paymentTerms: cleanedTerms }
                        : item,
                );
                await api.patch(`/work-orders/${id}/boq`, { boqItems: updated });
                setBoqItems(updated);
                swal("Success", "BOQ item payment terms saved successfully", "success");
                setShowPaymentTerms(false);
                setPaymentTermTargetIndex(null);
                return;
            }

            // ── Case 2: Saving from group terms editor ──────────────────────────
            // (Not needed in two-panel — terms auto-save in state, final save via Case 4)

            // ── Case 3: Saving global fallback terms editor ─────────────────────
            // (Not needed in two-panel — terms auto-save in state, final save via Case 4)

            // ── Case 4: Final save ───────────────────────────────────────────────
            await api.put(`/work-orders/${id}/payment-terms`, {
                paymentMode,
                paymentTerms: cleanedTerms,
                advanceAmount: hasAdvance ? Number(advanceAmount || 0) : 0,
                advanceDescription: hasAdvance ? String(advanceDescription || "").trim() : "",
                paymentGroups: paymentGroups.map((g) => ({
                    groupName: g.groupName,
                    paymentMode: g.paymentMode || "STANDARD",
                    boqItemIds: g.boqItemIds || [],
                    paymentTerms: normalizeTerms(g.paymentTerms),
                })),
            });

            swal("Success", "Payment terms saved successfully", "success");
            setShowPaymentTerms(false);
        } catch (err) {
            swal("Error", err.response?.data?.message || "Failed to save payment terms", "error");
        }
    };

    // ---------------------------------------------------
    // GROUP HELPERS
    // ---------------------------------------------------
    const addGroup = () => {
        setPaymentGroups([
            ...paymentGroups,
            { groupName: `Group ${paymentGroups.length + 1}`, paymentMode: "STANDARD", boqItemIds: [], paymentTerms: [{ stage: "", name: "", percentage: 0 }] },
        ]);
    };

    const removeGroup = (index) => {
        setPaymentGroups(paymentGroups.filter((_, i) => i !== index));
    };

    const updateGroupName = (index, name) => {
        const updated = [...paymentGroups];
        updated[index] = { ...updated[index], groupName: name };
        setPaymentGroups(updated);
    };

    // Toggle a BOQ item in/out of a group (removes from other groups first)
    const toggleItemInGroup = (groupIndex, boqItemId) => {
        const itemKey = String(boqItemId);
        const updated = paymentGroups.map((g, i) => {
            const ids = (g.boqItemIds || []).map(String);
            if (i === groupIndex) {
                // add or remove from this group
                return {
                    ...g,
                    boqItemIds: ids.includes(itemKey)
                        ? ids.filter((x) => x !== itemKey)
                        : [...ids, itemKey],
                };
            }
            // Remove from all other groups (an item can only belong to one group)
            return { ...g, boqItemIds: ids.filter((x) => x !== itemKey) };
        });
        setPaymentGroups(updated);
    };

    // Which group is this item in? Returns group index or -1
    const getItemGroupIndex = (boqItemId) => {
        if (!boqItemId) return -1;
        const key = String(boqItemId);
        return paymentGroups.findIndex((g) =>
            (g.boqItemIds || []).map(String).includes(key)
        );
    };

    // ---------------------------------------------------
    // CALCULATE TOTALS (ADDED)
    // ---------------------------------------------------
    const grandTotal = boqItems.reduce(
        (acc, item) => acc + item.quantity * item.price,
        0,
    );
    // ---------------------------------------------------
    // LOAD PAYMENT TERMS
    // ---------------------------------------------------
    const loadPaymentTerms = async ({ applyToState = true } = {}) => {
        try {
            const res = await api.get(`/work-orders/${id}/payment-terms`);

            if (res.data.success && res.data.data) {
                const data = res.data.data;

                if (data.paymentTerms) {
                    const loadedMode = data.paymentMode || "STANDARD";
                    const loadedTerms = normalizeTerms(data.paymentTerms);
                    const loadedGroups = Array.isArray(data.paymentGroups) ? data.paymentGroups : [];

                    if (applyToState) {
                        setPaymentMode(loadedMode);
                        setPaymentTerms(loadedTerms.length ? loadedTerms : defaultPaymentTerms);
                        setPaymentGroups(loadedGroups);
                    }

                    return { paymentMode: loadedMode, paymentTerms: loadedTerms, paymentGroups: loadedGroups };
                }

                // ✅ BACKWARD COMPATIBILITY (old API returning array only)
                if (Array.isArray(data) && data.length > 0) {
                    const loadedTerms = normalizeTerms(data);
                    if (applyToState) {
                        setPaymentMode("STANDARD");
                        setPaymentTerms(loadedTerms.length ? loadedTerms : defaultPaymentTerms);
                        setPaymentGroups([]);
                    }
                    return { paymentMode: "STANDARD", paymentTerms: loadedTerms, paymentGroups: [] };
                }
            }

            // ✅ DEFAULT WHEN NO TERMS FOUND
            if (applyToState) {
                setPaymentMode("STANDARD");
                setPaymentTerms(defaultPaymentTerms);
                setPaymentGroups([]);
            }
            return { paymentMode: "STANDARD", paymentTerms: [], paymentGroups: [] };
        } catch (err) {
            console.error(err);
            if (applyToState) {
                swal("Error", "Failed to load payment terms", "error");
                setPaymentMode("STANDARD");
                setPaymentTerms(defaultPaymentTerms);
                setPaymentGroups([]);
            }
            return { paymentMode: "STANDARD", paymentTerms: [], paymentGroups: [] };
        }
    };

    const openGlobalPaymentTerms = async () => {
        setPaymentTermTargetIndex(null);
        await loadPaymentTerms();
        setActiveGroupIndex(null);
        setShowPaymentTerms(true);
    };

    const openItemPaymentTerms = async (index) => {
        const globalConfig = await loadPaymentTerms({ applyToState: false });
        const item = boqItems[index] || {};
        const itemTerms = normalizeTerms(item.paymentTerms);

        // Group fallback — if item belongs to a payment group
        const groups = globalConfig?.paymentGroups || [];
        const itemKey = String(item._id);
        const assignedGroup = item._id
            ? groups.find((g) => (g.boqItemIds || []).map(String).includes(itemKey))
            : null;

        const groupTerms = assignedGroup ? normalizeTerms(assignedGroup.paymentTerms) : [];
        const groupMode = assignedGroup?.paymentMode || globalConfig?.paymentMode || "STANDARD";

        const fallbackTerms = normalizeTerms(globalConfig?.paymentTerms);

        setPaymentTermTargetIndex(index);

        if (itemTerms.length) {
            setPaymentMode(item.paymentMode || globalConfig?.paymentMode || "STANDARD");
            setPaymentTerms(itemTerms);
        } else if (groupTerms.length) {
            setPaymentMode(groupMode);
            setPaymentTerms(groupTerms);
        } else if (fallbackTerms.length) {
            setPaymentMode(globalConfig?.paymentMode || "STANDARD");
            setPaymentTerms(fallbackTerms);
        } else {
            setPaymentMode("STANDARD");
            setPaymentTerms(defaultPaymentTerms);
        }

        setShowPaymentTerms(true);
    };
    useEffect(() => {
        const cgstAmt = (grandTotal * cgst) / 100;
        const sgstAmt = (grandTotal * sgst) / 100;
        const igstAmt = (grandTotal * igst) / 100;

        const total = grandTotal + cgstAmt + sgstAmt + igstAmt;

        setCgstAmount(cgstAmt);
        setSgstAmount(sgstAmt);
        setIgstAmount(igstAmt);
        setFinalTotal(total);
    }, [grandTotal, cgst, sgst, igst]);
    // ---------------------------------------------------
    // RENDER UI
    // ---------------------------------------------------
    return (
        <Fragment>
            <style>{`
        .table-responsive {
          overflow-x: auto;
        }
        .boq-table {
          width: 100%;
          min-width: 1600px;
          table-layout: fixed;
        }
        .boq-table thead th {
          background-color: #fff !important;
          color: #333 !important;
          font-weight: 600 !important;
          text-align: center !important;
          padding: 12px 6px !important;
          border-bottom: 2px solid #00A15D !important;
          font-size: 13px;
        }
        .boq-table tbody td {
          padding: 8px 6px !important;
          vertical-align: middle !important;
        }
        .form-control, .form-select {
          border-radius: 4px;
          border: 1px solid #e2e2e2;
          font-size: 13px;
        }
        .btn-primary { background-color: #00A15D; border-color: #00A15D; }
        .btn-primary:hover { background-color: #008a4f; border-color: #008a4f; }
        .text-primary { color: #00A15D !important; }
        .card { border: 1px solid #eee; box-shadow: none; }
        .bg-light-success { background-color: rgba(0, 161, 93, 0.05); }
      `}</style>

            <PageTitle activeMenu="BOQ" motherMenu="Work Order" />

            <Row>
                <Col lg={12}>
                    <Card>
                        <Card.Header className="bg-white border-bottom d-flex flex-wrap justify-content-between align-items-center gap-3">
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => navigate(projectFromUrl ? `/work-orders?project=${projectFromUrl}` : "/work-orders")}
                                >
                                    <i className="fas fa-arrow-left"></i>
                                </Button>
                                <h4 className="mb-0 fw-bold">BOQ - {projectName}</h4>
                            </div>

                            <div className="d-flex flex-wrap gap-2">
                                {selectedItems.length > 0 && (
                                    <Button variant="danger" size="sm" onClick={handleBulkDelete}>
                                        <i className="fas fa-trash me-1"></i> Delete ({selectedItems.length})
                                    </Button>
                                )}
                                <Button variant="primary" size="sm" onClick={addRow}>
                                    + Add Item
                                </Button>
                                <Button variant="warning" size="sm" className="text-white" onClick={openGlobalPaymentTerms}>
                                    Payment Terms
                                </Button>
                                <Button variant="success" size="sm" onClick={downloadBOQPDF}>
                                    PDF
                                </Button>
                                <Button variant="info" size="sm" className="text-white" onClick={downloadBOQExcel}>
                                    Excel
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={downloadSampleExcel}>
                                    Sample
                                </Button>
                                <Button variant="dark" size="sm" onClick={() => setShowBoqImport(true)}>
                                    Import PDF
                                </Button>
                                <div className="d-flex align-items-center border rounded px-2 bg-light">
                                    <i className="fas fa-upload text-muted me-2 small"></i>
                                    <Form.Control type="file" accept=".xlsx,.xls" onChange={importExcel} className="border-0 bg-transparent p-0" style={{ width: "120px", fontSize: '11px' }} />
                                </div>
                            </div>
                        </Card.Header>

                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table bordered hover className="boq-table mb-0">
                                    <thead>
                                        <tr>
                                            <th style={{ width: "40px" }}>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={boqItems.length > 0 && boqItems.filter(i => i._id).length > 0 && selectedItems.length === boqItems.filter(i => i._id).length}
                                                    onChange={handleSelectAll}
                                                />
                                            </th>
                                            <th style={{ width: "45px" }}>#</th>
                                            <th style={{ width: "90px" }}>WLINo</th>
                                            <th style={{ width: "110px" }}>HSN / SAC</th>
                                            <th style={{ width: "250px", minWidth: "200px" }}>Item Name</th>
                                            <th style={{ width: "380px", minWidth: "300px" }}>Description</th>
                                            <th style={{ width: "110px" }}>UOM</th>
                                            <th style={{ width: "100px", minWidth: "90px" }}>Qty</th>
                                            <th style={{ width: "120px", minWidth: "110px" }}>Price</th>
                                            <th style={{ width: "120px" }}>Total</th>
                                            <th style={{ width: "90px" }}>Terms</th>
                                            <th style={{ width: "45px" }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {boqItems.length > 0 ? (
                                            paginatedItems.map((item, i) => (
                                                <tr key={i}>
                                                    <td className="text-center">
                                                        {item._id && (
                                                            <Form.Check
                                                                type="checkbox"
                                                                checked={selectedItems.includes(item._id)}
                                                                onChange={() => handleSelectOne(item._id)}
                                                            />
                                                        )}
                                                    </td>
                                                    <td className="text-center fw-bold">{(currentPage - 1) * pageSize + i + 1}</td>
                                                    <td>
                                                        <Form.Control value={item.wlino} onChange={(e) => updateField((currentPage - 1) * pageSize + i, "wlino", e.target.value)} />
                                                    </td>
                                                    <td>
                                                        <Form.Control value={item.hsnSac} onChange={(e) => updateField((currentPage - 1) * pageSize + i, "hsnSac", e.target.value)} />
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-1">
                                                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                                <Form.Control
                                                                    value={item.itemName}
                                                                    onChange={(e) => updateField((currentPage - 1) * pageSize + i, "itemName", e.target.value)}
                                                                    placeholder="Item Name"
                                                                />
                                                            </div>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="px-2 d-flex align-items-center justify-content-center flex-shrink-0 shadow-none"
                                                                style={{ fontSize: "11px", height: "38px", borderRadius: "4px" }}
                                                                onClick={() => openItemNameModal((currentPage - 1) * pageSize + i)}
                                                                title="View & Edit Full Item Name"
                                                            >
                                                                <i className="fas fa-eye" style={{ fontSize: "11px" }}></i>
                                                            </Button>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-1">
                                                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                                <Form.Control
                                                                    value={item.description}
                                                                    onChange={(e) => updateField((currentPage - 1) * pageSize + i, "description", e.target.value)}
                                                                    placeholder="Item Description"
                                                                />
                                                            </div>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="px-2 d-flex align-items-center justify-content-center flex-shrink-0 shadow-none"
                                                                style={{ fontSize: "11px", height: "38px", borderRadius: "4px" }}
                                                                onClick={() => openDescriptionModal((currentPage - 1) * pageSize + i)}
                                                                title="View & Edit Full Description"
                                                            >
                                                                <i className="fas fa-eye" style={{ fontSize: "11px" }}></i>
                                                            </Button>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Form.Select value={item.unit} onChange={(e) => updateField((currentPage - 1) * pageSize + i, "unit", e.target.value)}>
                                                            <option value="">UOM</option>
                                                            {units.map(u => <option key={u._id} value={u.symbol || u.name}>{u.name}</option>)}
                                                        </Form.Select>
                                                    </td>
                                                    <td style={{ width: "120px", minWidth: "120px" }}>
                                                        <Form.Control
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => updateField((currentPage - 1) * pageSize + i, "quantity", e.target.value)}
                                                            style={{ minWidth: "96px" }}
                                                        />
                                                    </td>
                                                    <td style={{ width: "140px", minWidth: "140px" }}>
                                                        <Form.Control
                                                            type="number"
                                                            value={item.price}
                                                            onChange={(e) => updateField((currentPage - 1) * pageSize + i, "price", e.target.value)}
                                                            style={{ minWidth: "110px" }}
                                                        />
                                                    </td>
                                                    <td className="text-end fw-bold">₹ {(item.quantity * item.price).toLocaleString()}</td>
                                                    <td className="text-center">
                                                        <div className="d-flex flex-column align-items-center gap-1">
                                                            <Button
                                                                variant={
                                                                    normalizeTerms(item.paymentTerms).length
                                                                        ? "outline-success"
                                                                        : item._id && getItemGroupIndex(item._id) >= 0
                                                                            ? "outline-primary"
                                                                            : "outline-secondary"
                                                                }
                                                                size="sm"
                                                                onClick={() => openItemPaymentTerms((currentPage - 1) * pageSize + i)}
                                                            >
                                                                Terms
                                                            </Button>
                                                            {item._id && getItemGroupIndex(item._id) >= 0 && (
                                                                <span className="badge bg-primary" style={{ fontSize: "10px" }}>
                                                                    {paymentGroups[getItemGroupIndex(item._id)]?.groupName}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <Button variant="link" className="text-danger p-0 shadow-none" onClick={() => removeRow((currentPage - 1) * pageSize + i)}>
                                                            <i className="fas fa-trash-alt"></i>
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="12" className="text-center py-5 text-muted">No items found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                    {boqItems.length > 0 && (
                                        <tfoot>
                                            <tr className="bg-light">
                                                <td colSpan="9" className="text-end fw-bold">Grand Total:</td>
                                                <td className="text-end fw-bold text-primary">₹ {grandTotal.toLocaleString()}</td>
                                                <td></td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </Table>
                            </div>

                            <div className="p-3 d-flex flex-wrap justify-content-between align-items-center border-top gap-3">
                                <div className="d-flex align-items-center gap-2">
                                    {boqItems.length > pageSize && (
                                        <div className="pagination-simple d-flex gap-1">
                                            <Button size="sm" variant="outline-secondary" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Prev</Button>
                                            <span className="px-3 py-1 border rounded bg-white fs-13">Page {currentPage} of {totalPages}</span>
                                            <Button size="sm" variant="outline-secondary" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Next</Button>
                                        </div>
                                    )}
                                </div>

                                <div className="d-flex align-items-center gap-4">
                                    <div className="text-end">
                                        <div className="fs-12 text-muted">Final Total (Incl. GST)</div>
                                        <div className="fs-18 fw-bold text-primary">₹ {finalTotal.toLocaleString()}</div>
                                    </div>
                                    <Button variant="primary" className="px-4 py-2 fw-bold" onClick={saveBOQ}>SAVE BOQ</Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* GST SUMMARY BOX (OPTIONAL INLINE OR CARD) */}
            {boqItems.length > 0 && (
                <Row className="mt-3 justify-content-end">
                    <Col md={4}>
                        <Card>
                            <Card.Body className="p-3">
                                <h6 className="fw-bold mb-3 border-bottom pb-2">Tax Breakdown</h6>
                                <div className="d-flex justify-content-between mb-2 fs-13">
                                    <span>Subtotal:</span>
                                    <span>₹ {grandTotal.toLocaleString()}</span>
                                </div>
                                {cgst > 0 && <div className="d-flex justify-content-between mb-1 fs-13"><span>CGST ({cgst}%):</span><span>₹ {cgstAmount.toLocaleString()}</span></div>}
                                {sgst > 0 && <div className="d-flex justify-content-between mb-1 fs-13"><span>SGST ({sgst}%):</span><span>₹ {sgstAmount.toLocaleString()}</span></div>}
                                {igst > 0 && <div className="d-flex justify-content-between mb-1 fs-13"><span>IGST ({igst}%):</span><span>₹ {igstAmount.toLocaleString()}</span></div>}
                                <hr />
                                <div className="d-flex justify-content-between align-items-center">
                                    <strong className="fs-14">Net Amount:</strong>
                                    <strong className="fs-16 text-primary">₹ {finalTotal.toLocaleString()}</strong>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            <BoqImportModal
                show={showBoqImport}
                onClose={() => setShowBoqImport(false)}
                onApply={handleApplyImportedBoq}
                sacCode={sacCode}
            />

            {/* PAYMENT TERMS MODAL */}
            {showPaymentTerms && (
                <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className={`modal-dialog modal-dialog-centered modal-dialog-scrollable ${paymentTermTargetIndex !== null ? "modal-lg" : "modal-xl"}`}>
                        <div className="modal-content border-0">

                            {/* HEADER */}
                            <div className="modal-header border-bottom py-3">
                                <h5 className="modal-title fw-bold mb-0">
                                    {paymentTermTargetIndex !== null
                                        ? `Payment Terms — ${boqItems[paymentTermTargetIndex]?.itemName || "BOQ Item"}`
                                        : "Payment Groups & Terms"}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => { setShowPaymentTerms(false); setPaymentTermTargetIndex(null); setActiveGroupIndex(null); }}></button>
                            </div>

                            <div className="modal-body p-0">

                                {/* CASE A: Individual BOQ item terms */}
                                {paymentTermTargetIndex !== null && (
                                    <div className="p-4">
                                        <div className="d-flex gap-2 mb-3">
                                            {["STANDARD", "CUSTOM"].map((m) => (
                                                <button key={m} type="button"
                                                    onClick={() => { setPaymentMode(m); setPaymentTerms([{ stage: "", name: "", percentage: 0 }]); }}
                                                    className={`btn btn-sm ${paymentMode === m ? "btn-primary" : "btn-outline-secondary"}`}>
                                                    {m === "STANDARD" ? "Standard Stages" : "Custom Milestones"}
                                                </button>
                                            ))}
                                        </div>
                                        {(() => {
                                            const targetItem = boqItems[paymentTermTargetIndex];
                                            const targetItemTotal = (Number(targetItem?.quantity) || 0) * (Number(targetItem?.price) || 0);
                                            return (
                                                <>
                                                    {targetItemTotal > 0 && (
                                                        <div className="alert alert-light py-2 px-3 mb-3 border fs-13 d-flex justify-content-between align-items-center">
                                                            <span><strong>Item Total:</strong> ₹ {targetItemTotal.toLocaleString()}</span>
                                                            <span className="text-muted fs-12">Amounts below calculate automatically on %</span>
                                                        </div>
                                                    )}
                                                    {paymentTerms.map((row, i) => (
                                                        <div key={i} className="d-flex align-items-center gap-2 mb-2">
                                                            <div className="flex-grow-1">
                                                                {paymentMode === "STANDARD" ? (
                                                                    <Form.Select value={row.stage || ""} onChange={(e) => updatePaymentTerm(i, "stage", e.target.value)}>
                                                                        <option value="">Select Stage</option>
                                                                        {paymentStages.map((s) => <option key={s} value={s}>{s}</option>)}
                                                                    </Form.Select>
                                                                ) : (
                                                                    <Form.Control placeholder="Milestone name..." value={row.name || ""} onChange={(e) => updatePaymentTerm(i, "name", e.target.value)} />
                                                                )}
                                                            </div>
                                                            <div style={{ width: "90px" }}>
                                                                <Form.Control type="number" className="text-center fw-bold" placeholder="%" value={row.percentage} onChange={(e) => updatePaymentTerm(i, "percentage", e.target.value)} />
                                                            </div>
                                                            {targetItemTotal > 0 && (
                                                                <span className="badge bg-light text-dark border fs-12 px-2 py-2" style={{ minWidth: "110px", textAlign: "right" }} title="Calculated Amount based on %">
                                                                    ₹ {((targetItemTotal * Number(row.percentage || 0)) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                </span>
                                                            )}
                                                            <button type="button" className="btn btn-link text-danger p-0" onClick={() => removePaymentRow(i)} disabled={paymentTerms.length === 1}>
                                                                <i className="fas fa-times"></i>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </>
                                            );
                                        })()}
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <button type="button" className="btn btn-link text-primary p-0 fw-semibold text-decoration-none" onClick={addPaymentRow}>+ Add Row</button>
                                            <span className={`badge fs-13 px-3 py-2 ${totalPercentage === 100 ? "bg-success" : "bg-danger"}`}>{totalPercentage}%</span>
                                        </div>
                                    </div>
                                )}

                                {/* CASE B: Two-panel — Groups list + inline terms editor */}
                                {paymentTermTargetIndex === null && (
                                    <div className="d-flex" style={{ minHeight: "460px" }}>

                                        {/* LEFT: Groups sidebar */}
                                        <div className="border-end bg-light" style={{ width: "260px", minWidth: "260px", overflowY: "auto" }}>

                                            {/* Default fallback row */}
                                            <div
                                                className={`p-3 border-bottom ${activeGroupIndex === null ? "bg-white border-start border-primary border-3" : ""}`}
                                                style={{ cursor: "pointer" }}
                                                onClick={() => setActiveGroupIndex(null)}
                                            >
                                                <div className="fw-semibold fs-13">Default Terms</div>
                                                <div className="text-muted mt-1" style={{ fontSize: "11px" }}>
                                                    {normalizeTerms(paymentTerms).length
                                                        ? normalizeTerms(paymentTerms).map((t) => `${t.stage || t.name} ${t.percentage}%`).join(" · ")
                                                        : <span className="text-danger">Not configured</span>}
                                                </div>
                                                <span className="badge bg-secondary mt-1" style={{ fontSize: "10px" }}>Fallback</span>
                                            </div>

                                            {/* Group rows */}
                                            {paymentGroups.map((group, gIdx) => {
                                                const assignedCount = (group.boqItemIds || []).length;
                                                const termsTotal = normalizeTerms(group.paymentTerms).reduce((s, t) => s + t.percentage, 0);
                                                return (
                                                    <div key={gIdx}
                                                        className={`p-3 border-bottom ${activeGroupIndex === gIdx ? "bg-white border-start border-primary border-3" : ""}`}
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => setActiveGroupIndex(gIdx)}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-start">
                                                            <div className="flex-grow-1 me-1" onClick={(e) => e.stopPropagation()}>
                                                                <input
                                                                    className="fw-semibold fs-13 border-0 bg-transparent p-0 w-100"
                                                                    value={group.groupName}
                                                                    onChange={(e) => updateGroupName(gIdx, e.target.value)}
                                                                    placeholder="Group name..."
                                                                    style={{ outline: "none" }}
                                                                />
                                                            </div>
                                                            <button type="button" className="btn btn-link text-danger p-0"
                                                                style={{ fontSize: "11px" }}
                                                                onClick={(e) => { e.stopPropagation(); removeGroup(gIdx); if (activeGroupIndex === gIdx) setActiveGroupIndex(null); }}>
                                                                <i className="fas fa-trash-alt"></i>
                                                            </button>
                                                        </div>
                                                        <div className="mt-1" style={{ fontSize: "11px" }}>
                                                            <span className="text-muted">{assignedCount} item{assignedCount !== 1 ? "s" : ""}</span>
                                                            <span className="mx-1 text-muted">·</span>
                                                            {termsTotal === 100
                                                                ? <span className="text-success">Terms ✓</span>
                                                                : <span className="text-danger">Terms {termsTotal}%</span>}
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            <div className="p-3">
                                                <button type="button" className="btn btn-outline-secondary btn-sm w-100" onClick={addGroup}>
                                                    <i className="fas fa-plus me-1"></i> Add Group
                                                </button>
                                            </div>
                                        </div>

                                        {/* RIGHT: Terms editor + item assignment */}
                                        <div className="flex-grow-1 p-4" style={{ overflowY: "auto" }}>
                                            {(() => {
                                                const isGroup = activeGroupIndex !== null;
                                                const currentTerms = isGroup ? (paymentGroups[activeGroupIndex]?.paymentTerms || []) : paymentTerms;
                                                const currentMode = isGroup ? (paymentGroups[activeGroupIndex]?.paymentMode || "STANDARD") : paymentMode;
                                                const total = currentTerms.reduce((s, r) => s + Number(r.percentage || 0), 0);

                                                const updateTerm = (i, field, value) => {
                                                    if (isGroup) {
                                                        const updated = [...paymentGroups];
                                                        const terms = [...(updated[activeGroupIndex].paymentTerms || [])];
                                                        terms[i] = { ...terms[i], [field]: field === "percentage" ? Number(value) : value };
                                                        updated[activeGroupIndex] = { ...updated[activeGroupIndex], paymentTerms: terms };
                                                        setPaymentGroups(updated);
                                                    } else { updatePaymentTerm(i, field, value); }
                                                };
                                                const addTerm = () => {
                                                    if (isGroup) {
                                                        const updated = [...paymentGroups];
                                                        updated[activeGroupIndex] = { ...updated[activeGroupIndex], paymentTerms: [...currentTerms, { stage: "", name: "", percentage: 0 }] };
                                                        setPaymentGroups(updated);
                                                    } else { addPaymentRow(); }
                                                };
                                                const removeTerm = (i) => {
                                                    if (isGroup) {
                                                        const updated = [...paymentGroups];
                                                        updated[activeGroupIndex] = { ...updated[activeGroupIndex], paymentTerms: currentTerms.filter((_, idx) => idx !== i) };
                                                        setPaymentGroups(updated);
                                                    } else { removePaymentRow(i); }
                                                };
                                                const changeMode = (m) => {
                                                    if (isGroup) {
                                                        const updated = [...paymentGroups];
                                                        updated[activeGroupIndex] = { ...updated[activeGroupIndex], paymentMode: m, paymentTerms: [{ stage: "", name: "", percentage: 0 }] };
                                                        setPaymentGroups(updated);
                                                    } else { setPaymentMode(m); setPaymentTerms([{ stage: "", name: "", percentage: 0 }]); }
                                                };

                                                return (
                                                    <>
                                                        {/* ADVANCE AMOUNT SECTION */}
                                                        {!isGroup && (
                                                            <div className="mb-3">
                                                                {!hasAdvance ? (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-primary btn-sm rounded-pill px-3 py-1 fw-semibold"
                                                                        onClick={() => {
                                                                            setHasAdvance(true);
                                                                        }}
                                                                    >
                                                                        <i className="fas fa-plus me-1"></i> Add Advance
                                                                    </button>
                                                                ) : (
                                                                    <div className="card border p-3 bg-light rounded-3 shadow-sm">
                                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                                            <label className="fw-bold fs-13 mb-0 text-dark">
                                                                                <i className="fas fa-hand-holding-usd text-primary me-2"></i>Advance Payment
                                                                            </label>
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                {Number(advanceAmount) > 0 && (
                                                                                    <span className="badge bg-primary fs-12 px-2 py-1">
                                                                                        Advance: ₹ {Number(advanceAmount).toLocaleString()}
                                                                                    </span>
                                                                                )}
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-link text-danger p-0 ms-2"
                                                                                    title="Remove Advance"
                                                                                    onClick={() => {
                                                                                        setHasAdvance(false);
                                                                                        setAdvanceAmount(0);
                                                                                        setAdvanceDescription("");
                                                                                    }}
                                                                                >
                                                                                    <i className="fas fa-trash-alt"></i>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        <Row className="g-2 align-items-end">
                                                                            <Col md={4}>
                                                                                <Form.Group>
                                                                                    <Form.Label className="fs-12 text-muted mb-1">Amount (₹)</Form.Label>
                                                                                    <div className="input-group">
                                                                                        <span className="input-group-text bg-white text-muted border-end-0" style={{ height: "38px", fontSize: "14px" }}>₹</span>
                                                                                        <Form.Control
                                                                                            type="number"
                                                                                            placeholder="0.00"
                                                                                            min="0"
                                                                                            className="border-start-0"
                                                                                            style={{ height: "38px", fontSize: "13px" }}
                                                                                            value={advanceAmount || ""}
                                                                                            onChange={(e) => setAdvanceAmount(e.target.value)}
                                                                                        />
                                                                                    </div>
                                                                                </Form.Group>
                                                                            </Col>
                                                                            <Col md={8}>
                                                                                <Form.Group>
                                                                                    <Form.Label className="fs-12 text-muted mb-1">Description</Form.Label>
                                                                                    <Form.Control
                                                                                        type="text"
                                                                                        placeholder="e.g. Advance amount with work order"
                                                                                        style={{ height: "38px", fontSize: "13px" }}
                                                                                        value={advanceDescription || ""}
                                                                                        onChange={(e) => setAdvanceDescription(e.target.value)}
                                                                                    />
                                                                                </Form.Group>
                                                                            </Col>
                                                                        </Row>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <h6 className="fw-bold mb-0">
                                                                {isGroup ? paymentGroups[activeGroupIndex]?.groupName || "Group" : "Default Terms"}
                                                                {!isGroup && <span className="badge bg-secondary ms-2 fs-11">Fallback</span>}
                                                            </h6>
                                                            <span className={`badge px-3 py-2 fs-13 ${total === 100 ? "bg-success" : "bg-danger"}`}>{total}%</span>
                                                        </div>

                                                        <div className="d-flex gap-2 mb-3">
                                                            {["STANDARD", "CUSTOM"].map((m) => (
                                                                <button key={m} type="button"
                                                                    onClick={() => changeMode(m)}
                                                                    className={`btn btn-sm ${currentMode === m ? "btn-primary" : "btn-outline-secondary"}`}>
                                                                    {m === "STANDARD" ? "Standard Stages" : "Custom Milestones"}
                                                                </button>
                                                            ))}
                                                        </div>

                                                        {(() => {
                                                            const baseTotal = isGroup
                                                                ? boqItems
                                                                    .filter((b) => (paymentGroups[activeGroupIndex]?.boqItemIds || []).map(String).includes(String(b._id)))
                                                                    .reduce((s, b) => s + (Number(b.quantity || 0) * Number(b.price || 0)), 0)
                                                                : grandTotal;

                                                            return (
                                                                <>
                                                                    {baseTotal > 0 && (
                                                                        <div className="alert alert-light py-2 px-3 mb-3 border fs-13 d-flex justify-content-between align-items-center">
                                                                            <span>
                                                                                <strong>{isGroup ? "Assigned Items Total:" : "Total BOQ Value:"}</strong> ₹ {baseTotal.toLocaleString()}
                                                                            </span>
                                                                            <span className="text-muted fs-12">Amounts below calculate automatically on %</span>
                                                                        </div>
                                                                    )}
                                                                    {currentTerms.map((row, i) => (
                                                                        <div key={i} className="d-flex align-items-center gap-2 mb-2">
                                                                            <div className="flex-grow-1">
                                                                                {currentMode === "STANDARD" ? (
                                                                                    <Form.Select value={row.stage || ""} onChange={(e) => updateTerm(i, "stage", e.target.value)}>
                                                                                        <option value="">Select Stage</option>
                                                                                        {paymentStages.map((s) => <option key={s} value={s}>{s}</option>)}
                                                                                    </Form.Select>
                                                                                ) : (
                                                                                    <Form.Control placeholder="Milestone name..." value={row.name || ""} onChange={(e) => updateTerm(i, "name", e.target.value)} />
                                                                                )}
                                                                            </div>
                                                                            <div style={{ width: "90px" }}>
                                                                                <Form.Control type="number" className="text-center fw-bold" placeholder="%" value={row.percentage} onChange={(e) => updateTerm(i, "percentage", e.target.value)} />
                                                                            </div>
                                                                            {baseTotal > 0 && (
                                                                                <span className="badge bg-light text-dark border fs-12 px-2 py-2" style={{ minWidth: "110px", textAlign: "right" }} title="Calculated Amount based on %">
                                                                                    ₹ {((baseTotal * Number(row.percentage || 0)) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                </span>
                                                                            )}
                                                                            <button type="button" className="btn btn-link text-danger p-0" onClick={() => removeTerm(i)} disabled={currentTerms.length === 1}>
                                                                                <i className="fas fa-times"></i>
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </>
                                                            );
                                                        })()}
                                                        <button type="button" className="btn btn-link text-primary p-0 fw-semibold text-decoration-none mb-3" onClick={addTerm}>+ Add Row</button>

                                                        {isGroup && (
                                                            <>
                                                                <hr className="my-3" />
                                                                <div className="fw-semibold fs-13 mb-2">Assign BOQ Items</div>
                                                                {boqItems.filter((item) => item._id).length === 0
                                                                    ? <p className="text-muted fst-italic fs-12">Save BOQ first to assign items</p>
                                                                    : (
                                                                        <div className="d-flex flex-wrap gap-2">
                                                                            {boqItems.filter((item) => item._id).map((item) => {
                                                                                const assignedGIdx = getItemGroupIndex(item._id);
                                                                                const isInThis = assignedGIdx === activeGroupIndex;
                                                                                const isInOther = assignedGIdx >= 0 && assignedGIdx !== activeGroupIndex;
                                                                                return (
                                                                                    <button key={item._id} type="button"
                                                                                        title={isInOther ? `In "${paymentGroups[assignedGIdx]?.groupName}"` : ""}
                                                                                        onClick={() => !isInOther && toggleItemInGroup(activeGroupIndex, item._id)}
                                                                                        className={`btn btn-sm ${isInThis ? "btn-success" : isInOther ? "btn-light text-muted" : "btn-outline-secondary"}`}
                                                                                        disabled={isInOther}
                                                                                        style={{ fontSize: "12px" }}
                                                                                    >
                                                                                        {isInThis && <i className="fas fa-check me-1" style={{ fontSize: "10px" }}></i>}
                                                                                        {item.itemName || `Item ${boqItems.indexOf(item) + 1}`}
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}
                                                            </>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>

                                    </div>
                                )}
                            </div>

                            {/* FOOTER */}
                            <div className="modal-footer bg-light border-top">
                                <Button variant="secondary" onClick={() => { setShowPaymentTerms(false); setPaymentTermTargetIndex(null); setActiveGroupIndex(null); }}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    disabled={paymentTermTargetIndex !== null && totalPercentage !== 100}
                                    onClick={savePaymentTerms}
                                >
                                    Save
                                </Button>
                            </div>

                        </div>
                    </div>
                </div>
            )}

            {/* ITEM NAME VIEW / EDIT MODAL */}
            <Modal
                show={itemNameModal.show}
                onHide={() => setItemNameModal({ show: false, itemIndex: null, itemNumber: null, itemName: "" })}
                size="lg"
                centered
            >
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="h5 mb-0 d-flex align-items-center gap-2">
                        <i className="fas fa-edit text-primary"></i>
                        <span>Edit Item Name — <strong>Item #{itemNameModal.itemNumber}</strong></span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Form.Group className="mb-0">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="fw-bold mb-0 text-dark">
                                Item Name
                            </Form.Label>
                            <span className="badge bg-light text-muted border">
                                Editable
                            </span>
                        </div>
                        <Form.Control
                            as="textarea"
                            rows={6}
                            value={itemNameModal.itemName}
                            onChange={(e) =>
                                setItemNameModal((prev) => ({
                                    ...prev,
                                    itemName: e.target.value,
                                }))
                            }
                            placeholder="Enter complete item name here..."
                            style={{
                                fontSize: "13.5px",
                                lineHeight: "1.6",
                                whiteSpace: "pre-wrap",
                                fontFamily: "inherit",
                                padding: "12px",
                                borderRadius: "6px",
                                border: "1px solid #ced4da",
                            }}
                        />
                        <div className="d-flex justify-content-between mt-2 text-muted small">
                            <span><i className="fas fa-align-left me-1"></i>Lines: {(itemNameModal.itemName || "").split("\n").length}</span>
                            <span><i className="fas fa-font me-1"></i>Characters: {(itemNameModal.itemName || "").length}</span>
                        </div>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="bg-light border-top">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                            setItemNameModal({ show: false, itemIndex: null, itemNumber: null, itemName: "" })
                        }
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSaveItemNameModal}
                    >
                        <i className="fas fa-check me-1"></i> Save Item Name
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* DESCRIPTION VIEW / EDIT MODAL */}
            <Modal
                show={descriptionModal.show}
                onHide={() => setDescriptionModal({ show: false, itemIndex: null, itemName: "", description: "" })}
                size="lg"
                centered
            >
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="h5 mb-0 d-flex align-items-center gap-2">
                        <i className="fas fa-edit text-primary"></i>
                        <span>Edit Description — <strong>{descriptionModal.itemName || "BOQ Item"}</strong></span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Form.Group className="mb-0">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="fw-bold mb-0 text-dark">
                                Item Description
                            </Form.Label>
                            <span className="badge bg-light text-muted border">
                                Editable
                            </span>
                        </div>
                        <Form.Control
                            as="textarea"
                            rows={12}
                            value={descriptionModal.description}
                            onChange={(e) =>
                                setDescriptionModal((prev) => ({
                                    ...prev,
                                    description: e.target.value,
                                }))
                            }
                            placeholder="Enter complete description here..."
                            style={{
                                fontSize: "13.5px",
                                lineHeight: "1.6",
                                whiteSpace: "pre-wrap",
                                fontFamily: "inherit",
                                padding: "12px",
                                borderRadius: "6px",
                                border: "1px solid #ced4da",
                            }}
                        />
                        <div className="d-flex justify-content-between mt-2 text-muted small">
                            <span><i className="fas fa-align-left me-1"></i>Lines: {(descriptionModal.description || "").split("\n").length}</span>
                            <span><i className="fas fa-font me-1"></i>Characters: {(descriptionModal.description || "").length}</span>
                        </div>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="bg-light border-top">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                            setDescriptionModal({ show: false, itemIndex: null, itemName: "", description: "" })
                        }
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSaveDescriptionModal}
                    >
                        <i className="fas fa-check me-1"></i> Save Description
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

export default WorkOrderBOQ;
